import { filasPorId } from "@/lib/catalogos-cache.server";
import { publicationPayload } from "@/lib/publicacion-mode";
import { requireVipAccess, getVipAccess } from "./vip-access.server";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { Json } from "@/integrations/supabase/types";
import { getSupabaseAdmin } from "@/integrations/external-supabase/client.server";
import { resolveCurrentUserId } from "@/lib/articulos.server";
import { searchTerms } from "@/lib/search-text";
import { currentUserAgenciaIds, userIsStaff } from "@/lib/anuncios-access.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  agenciaDeContacto,
  contactoDeUsuario,
  syncAnuncioContactos,
  syncAnuncioUsosLocal,
  processAddressComponents,
  resolveAddressFromString,
} from "@/lib/anuncios.server";
import { applyAsignadoFilter } from "@/lib/scope-filtro";

const Id = z.object({ id: z.string().uuid() });

// ───────── Google Maps: clave para autocomplete en navegador ─────────
export const getGoogleMapsBrowserKey = createServerFn({ method: "GET" }).handler(
  async () => {
    const key = process.env.GOOGLE_MAPS_BROWSER_KEY ?? "";
    return { key };
  },
);

// ───────── Autocomplete de direcciones (via gateway, sin restricción de referrer) ─────────
export const autocompleteAddress = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        input: z.string().trim().min(2).max(200),
        sessionToken: z.string().trim().min(1).max(100).optional(),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const lovableKey = process.env.LOVABLE_API_KEY;
    const connKey =
      process.env.GOOGLE_MAPS_API_KEY_1 ?? process.env.GOOGLE_MAPS_API_KEY;
    if (!lovableKey || !connKey)
      throw new Error("Google Maps connector no configurado");
    const res = await fetch(
      "https://connector-gateway.lovable.dev/google_maps/places/v1/places:autocomplete",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${lovableKey}`,
          "X-Connection-Api-Key": connKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          input: data.input,
          languageCode: "es",
          regionCode: "es",
          includedRegionCodes: ["es", "it"],
          ...(data.sessionToken ? { sessionToken: data.sessionToken } : {}),
        }),
      },
    );
    if (!res.ok) {
      console.warn(`Autocomplete HTTP ${res.status}: ${await res.text()}`);
      return [];
    }
    const json = (await res.json()) as {
      suggestions?: Array<{
        placePrediction?: {
          placeId?: string;
          text?: { text?: string };
          structuredFormat?: {
            mainText?: { text?: string };
            secondaryText?: { text?: string };
          };
        };
      }>;
    };
    return (json.suggestions ?? [])
      .map((s) => {
        const p = s.placePrediction;
        if (!p) return null;
        return {
          placeId: p.placeId ?? "",
          primary: p.structuredFormat?.mainText?.text ?? p.text?.text ?? "",
          secondary: p.structuredFormat?.secondaryText?.text ?? "",
          full: p.text?.text ?? "",
        };
      })
      .filter((x): x is NonNullable<typeof x> => !!x);
  });

// ───────── Resolución de direcciones (delegada a helpers reutilizables) ─────────

export const processGoogleAddress = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        formatted_address: z.string().trim().min(3).max(500),
        lat: z.number(),
        lng: z.number(),
        components: z
          .array(
            z.object({
              long_name: z.string().trim().min(1).max(200),
              short_name: z.string().trim().min(1).max(50),
              types: z.array(z.string().trim().min(1).max(80)).max(20),
            }),
          )
          .min(1)
          .max(40),
      })
      .parse(d),
  )
  .handler(async ({ data }) =>
    processAddressComponents({
      components: data.components,
      lat: data.lat,
      lng: data.lng,
      formatted_address: data.formatted_address,
    }),
  );

export const geocodeAddress = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ address: z.string().trim().min(3).max(300) }).parse(d),
  )
  .handler(async ({ data }) => resolveAddressFromString(data.address));


// ───────── Catálogos públicos ─────────
export const ANUNCIO_ORIGENES = [
  "bizaclia", "idealista", "fotocasa", "wallapop", "ai", "otro", "alfainmo", "kyero",
] as const;
export const ANUNCIO_ESTADOS = [
  "pendiente", "publicado", "rechazado", "en_revision", "borrado", "no_publicado", "demo",
] as const;
/**
 * Estados que una agencia gestiona sobre sus propios anuncios. El resto
 * (rechazado, en revisión, demo) son decisiones del equipo de Bizaclia.
 */
export const ANUNCIO_ESTADOS_AGENCIA = [
  "pendiente", "publicado", "no_publicado", "borrado",
] as const satisfies readonly (typeof ANUNCIO_ESTADOS)[number][];
export function esEstadoAgencia(estado: string | null | undefined): boolean {
  return (ANUNCIO_ESTADOS_AGENCIA as readonly string[]).includes(estado ?? "");
}
export const ANUNCIO_PROCESOS = [
  "no_iniciado", "en_captacion", "captado", "operacion_iniciada", "operacion_completada", "cancelado",
] as const;
export const ANUNCIO_VISIBILIDADES = ["publico", "ciego", "off_market"] as const;
export const ESTADOS_INMUEBLE = [
  "obra_nueva", "reformado", "buen_estado", "por_reformar", "necesita_reforma",
] as const;
export const ESTADOS_ACTIVOS = ["bueno", "regular", "malo"] as const;
export const NIVELES_TRANSITO = [
  "muy_alto", "alto", "medio", "bajo", "muy_bajo",
] as const;
export const ENERGIA_CONSUMO = [
  "a_plus_plus", "a_plus", "a", "b", "c", "d", "e", "f", "g",
] as const;
export const ENERGIA_EMISIONES = ["a", "b", "c", "d", "e", "f", "g"] as const;
export const ANUNCIO_CONTACTO_ROLES = [
  "anunciante", "propietario", "arrendatario", "otro",
] as const;

// ───────── Geo ─────────
// PostgREST limita cada respuesta a 1000 filas aunque pidamos `.range(0, 49999)`
// (hay ~1900 ciudades y ~2000 barrios). Paginamos en bloques hasta agotar.
const GEO_PAGE = 1000;
async function fetchAllPages<T>(
  page: (
    from: number,
    to: number,
  ) => PromiseLike<{
    data: T[] | null;
    error: { message: string } | null;
  }>,
): Promise<T[]> {
  const out: T[] = [];
  for (let from = 0; from < 50000; from += GEO_PAGE) {
    const { data, error } = await page(from, from + GEO_PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    out.push(...batch);
    if (batch.length < GEO_PAGE) break;
  }
  return out;
}

export const listProvincias = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await getSupabaseAdmin()
    .from("provincias")
    .select("id, slug, nombre")
    .order("nombre")
    .range(0, 49999);
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const listComarcas = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await getSupabaseAdmin()
    .from("comarcas")
    .select("id, slug, nombre, provincia_id")
    .order("nombre")
    .order("id")
    .range(0, 9999);
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const listCiudades = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ provinciaId: z.string().uuid().optional() }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    return fetchAllPages((from, to) => {
      let q = getSupabaseAdmin()
        .from("ciudades")
        .select("id, slug, nombre, provincia_id, comarca_id")
        .order("nombre")
        .order("id")
        .range(from, to);
      if (data.provinciaId) q = q.eq("provincia_id", data.provinciaId);
      return q;
    });
  });

export const listDistritos = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ ciudadId: z.string().uuid().optional() }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    return fetchAllPages((from, to) => {
      let q = getSupabaseAdmin()
        .from("distritos")
        .select("id, slug, nombre, ciudad_id")
        .order("nombre")
        .order("id")
        .range(from, to);
      if (data.ciudadId) q = q.eq("ciudad_id", data.ciudadId);
      return q;
    });
  });

export const listBarrios = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ distritoId: z.string().uuid().optional() }).parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    return fetchAllPages((from, to) => {
      let q = getSupabaseAdmin()
        .from("barrios")
        .select("id, slug, nombre, distrito_id")
        .order("nombre")
        .order("id")
        .range(from, to);
      if (data.distritoId) q = q.eq("distrito_id", data.distritoId);
      return q;
    });
  });

// ───────── Contactos asociables ─────────
export const listContactosLite = createServerFn({ method: "GET" }).handler(async () => {
  const _uid = await resolveCurrentUserId();
  if (!_uid) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const isStaff = await userIsStaff(_uid);
  let q = sb
    .from("contactos")
    .select("id, nombre, apellidos, email, telefono, ciudad")
    .order("nombre")
    .range(0, 49999);
  if (!isStaff) {
    const { data: members } = await sb
      .from("agencia_miembros")
      .select("agencia_id")
      .eq("user_id", _uid);
    const agenciaIds = (members ?? []).map((m: { agencia_id: string }) => m.agencia_id);
    if (agenciaIds.length === 0) throw new Error("Forbidden");
    q = q.in("agencia_id", agenciaIds);
  }
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data ?? [];
});

export const getContactosLiteByIds = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => z.object({ ids: z.array(z.string().uuid()).max(200) }).parse(d))
  .handler(async ({ data }) => {
    const _uid = await resolveCurrentUserId();
    if (!_uid) throw new Error("Unauthorized");
    if (data.ids.length === 0) return [];
    // Vista de clientes: cartera propia + clientes cedidos (staff: todos).
    const { clientesPorIds } = await import("@/lib/clientes-cedidos.server");
    const mapa = await clientesPorIds(data.ids);
    return Array.from(mapa.values()).map(({ id, nombre, apellidos, email, telefono, ciudad }) => ({
      id,
      nombre,
      apellidos,
      email,
      telefono,
      ciudad,
    }));
  });


export const searchContactosLite = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        search: z.string().trim().max(100).optional().default(""),
        ids: z.array(z.string().uuid()).max(100).optional().default([]),
        limit: z.number().int().min(1).max(50).optional().default(10),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const _uid = await resolveCurrentUserId();
    if (!_uid) throw new Error("Unauthorized");
    const isStaff = await userIsStaff(_uid);
    const agenciaIds = isStaff ? [] : await currentUserAgenciaIds(_uid);
    if (!isStaff && agenciaIds.length === 0) throw new Error("Forbidden");
    const sb = getSupabaseAdmin();
    const terms = searchTerms(data.search);
    // Staff busca en `contactos`; una agencia, en la vista de clientes (su
    // cartera + los cedidos por sus casos), que ya limita las filas.
    const columns = isStaff
      ? "id, nombre, apellidos, email, telefono, ciudad, agencia_id"
      : "id, nombre, apellidos, email, telefono, ciudad, cedido";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fuente = () => (isStaff ? sb.from("contactos") : (sb as any).from("clientes_cedidos"));

    let matchesQ = fuente().select(columns).order("nombre", { ascending: true }).limit(data.limit);

    // Sin acentos y por palabras: "pablo sanchez" encuentra "Pablo Sánchez"
    for (const term of terms) {
      matchesQ = matchesQ.ilike("busqueda", `%${term}%`);
    }

    const { data: matches, error } = await matchesQ;
    if (error) throw new Error(error.message);

    const selectedIds = Array.from(new Set(data.ids));
    const selectedQ = selectedIds.length ? fuente().select(columns).in("id", selectedIds) : null;
    const selected = selectedQ
      ? await selectedQ
      : { data: [] as Array<{ id: string }>, error: null };
    if (selected.error) throw new Error(selected.error.message);

    const seen = new Set<string>();
    return [...(selected.data ?? []), ...(matches ?? [])].filter((row) => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
  });

// ───────── Búsqueda ligera de anuncios para asignación ─────────
export const searchAnunciosLite = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({
        search: z.string().trim().max(100).optional().default(""),
        ids: z.array(z.string().uuid()).max(100).optional().default([]),
        limit: z.number().int().min(1).max(50).optional().default(20),
      })
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    const _uid = await resolveCurrentUserId();
    if (!_uid) throw new Error("Unauthorized");
    const isStaff = await userIsStaff(_uid);
    const agenciaIds = isStaff ? [] : await currentUserAgenciaIds(_uid);
    if (!isStaff && agenciaIds.length === 0) throw new Error("Forbidden");
    const sb = getSupabaseAdmin();
    const columns =
      "id, referencia, alias, direccion_completa, agencia_id";
    const terms = searchTerms(data.search);

    let matchesQ = sb
      .from("anuncios")
      .select(columns)
      .order("created_at", { ascending: false })
      .limit(data.limit);
    if (!isStaff) matchesQ = matchesQ.in("agencia_id", agenciaIds);
    // Sin tildes: `busqueda` = referencia, alias, referencia_ext, dirección… normalizados
    for (const term of terms) {
      matchesQ = matchesQ.ilike("busqueda", `%${term}%`);
    }

    const { data: matches, error } = await matchesQ;
    if (error) throw new Error(error.message);

    const selectedIds = Array.from(new Set(data.ids));
    let selectedQ = selectedIds.length
      ? sb.from("anuncios").select(columns).in("id", selectedIds)
      : null;
    if (selectedQ && !isStaff) selectedQ = selectedQ.in("agencia_id", agenciaIds);
    const selected = selectedQ
      ? await selectedQ
      : { data: [] as Array<{ id: string }>, error: null };
    if (selected.error) throw new Error(selected.error.message);

    const seen = new Set<string>();
    return [...(selected.data ?? []), ...(matches ?? [])].filter((row) => {
      if (seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    });
  });

// ───────── Schema de input ─────────
const nullableText = z.string().trim().max(2000).nullable().optional();
const shortText = z.string().trim().max(200).nullable().optional();
const numberOrNull = z.number().finite().nullable().optional();
const intOrNull = z.number().int().nullable().optional();
const boolish = z.boolean().optional();

const ContactoRowInput = z.object({
  contacto_id: z.string().uuid(),
  rol: z.enum(ANUNCIO_CONTACTO_ROLES),
  rol_custom: z.string().trim().max(80).nullable().optional(),
});

const AnuncioInput = z.object({
  modo_publicacion: z.enum(["publico", "off_market"]).optional(),
  alias: shortText,
  referencia_ext: shortText,
  slug: z.string().trim().max(200).nullable().optional(),
  origen: z.enum(ANUNCIO_ORIGENES).default("bizaclia"),
  asignado_a: z.string().uuid().nullable().optional(),
  idealista_id: shortText,
  url_original: z.string().trim().max(2000).nullable().optional(),
  proceso: z.enum(ANUNCIO_PROCESOS).default("no_iniciado"),
  estado: z.enum(ANUNCIO_ESTADOS).default("pendiente"),
  visibilidad: z.enum(ANUNCIO_VISIBILIDADES).default("publico"),
  autoriza_intermediacion: boolish,
  rating: z.number().int().min(1).max(5).nullable().optional(),

  operacion_id: z.string().uuid().nullable().optional(),
  tipo_local_id: z.string().uuid().nullable().optional(),
  licencia_id: z.string().uuid().nullable().optional(),
  tipo_zona: shortText,
  tipo_operador: shortText,

  provincia_id: z.string().uuid().nullable().optional(),
  ciudad_id: z.string().uuid().nullable().optional(),
  distrito_id: z.string().uuid().nullable().optional(),
  barrio_id: z.string().uuid().nullable().optional(),
  calle: shortText,
  calle_id: z.string().uuid().nullable().optional(),
  numero: z.string().trim().max(20).nullable().optional(),
  cp: z.string().trim().max(10).nullable().optional(),
  direccion_completa: z.string().trim().max(500).nullable().optional(),
  direccion_visible: boolish,
  lat: numberOrNull,
  lng: numberOrNull,

  precio_alquiler: numberOrNull,
  precio_traspaso: numberOrNull,
  precio_venta: numberOrNull,
  importe_fianza: numberOrNull,
  facturacion_anual: numberOrNull,
  facturacion_mensual_promedio: numberOrNull,
  rent_bruta: numberOrNull,
  rent_neta: numberOrNull,
  periodo_obligado_cumplimiento: nullableText,
  contrato_fecha_inicio: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional().or(z.literal("").transform(() => null)),
  contrato_fecha_fin: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional().or(z.literal("").transform(() => null)),
  gastos_asociados: nullableText,
  garantias: nullableText,

  descripcion: z.string().max(20000).nullable().optional(),
  antiguedad_negocio: shortText,
  motivo_traspaso: nullableText,
  conectividad_accesos: nullableText,
  solvencia_grupo: nullableText,
  notas_internas: z.string().max(10000).nullable().optional(),
  nivel_transito: z.enum(NIVELES_TRANSITO).nullable().optional(),
  estado_inmueble: z.enum(ESTADOS_INMUEBLE).nullable().optional(),
  estado_activos: z.enum(ESTADOS_ACTIVOS).nullable().optional(),
  e_consumo: z.enum(ENERGIA_CONSUMO).nullable().optional(),
  e_emisiones: z.enum(ENERGIA_EMISIONES).nullable().optional(),
  equipamiento_incluido: boolish,

  h_aforo: intOrNull,
  h_camara_frigorifica: boolish,
  h_terraza: boolish,
  h_mesas_terraza: intOrNull,
  h_sillas_terraza: intOrNull,
  h_obrador: boolish,
  h_salida_humos: boolish,

  n_altura: numberOrNull,
  n_muelle_carga: boolish,
  n_vado: boolish,

  t_acceso_movilidad_reducida: boolish,
  t_agua: boolish,
  t_aire_acondicionado: boolish,
  t_amueblado: boolish,
  t_alarma: boolish,
  t_cocina: boolish,
  t_esquinero: boolish,
  t_mucha_luz: boolish,
  t_num_aseos: intOrNull,
  t_parking: boolish,
  t_parking_plazas: intOrNull,
  t_salida_emergencia: boolish,
  t_superficie: numberOrNull,
  t_ancho_fachada: numberOrNull,
  distribucion: z
    .array(
      z.object({
        titulo: z.string().trim().max(120),
        superficie: z.number().nonnegative().nullable().optional(),
      }),
    )
    .default([]),

  a_pie_de_calle: boolish,

  agencia_id: z.string().uuid().nullable().optional(),

  // Relaciones
  contactos: z.array(ContactoRowInput).default([]),
  uso_local_ids: z.array(z.string().uuid()).default([]),
});

export type AnuncioInput = z.infer<typeof AnuncioInput>;

// ───────── List ─────────
// Whitelist de campos booleanos permitidos como filtros para evitar inyección.
export const ANUNCIO_BOOL_FILTERS = [
  "autoriza_intermediacion",
  "equipamiento_incluido",
  "a_pie_de_calle",
  "h_camara_frigorifica",
  "h_terraza",
  "h_obrador",
  "h_salida_humos",
  "n_muelle_carga",
  "n_vado",
  "t_acceso_movilidad_reducida",
  "t_agua",
  "t_aire_acondicionado",
  "t_amueblado",
  "t_alarma",
  "t_cocina",
  "t_esquinero",
  "t_mucha_luz",
  "t_parking",
  "t_salida_emergencia",
] as const;
export type AnuncioBoolFilter = (typeof ANUNCIO_BOOL_FILTERS)[number];

const uuidArr = z.array(z.string().uuid()).optional();

const listAnunciosInput = z.object({
  /** Editor en modo "Todo el equipo" (solo lectura de lo de sus compañeros). */
  ver_equipo: z.boolean().optional(),
  estado: z.enum(ANUNCIO_ESTADOS).optional(),
  operacionId: z.string().uuid().optional(),
  ciudadId: z.string().uuid().optional(),
  q: z.string().trim().max(120).optional(),
  estados: z.array(z.enum(ANUNCIO_ESTADOS)).optional(),
  visibilidades: z.array(z.enum(ANUNCIO_VISIBILIDADES)).optional(),
  origenes: z.array(z.enum(ANUNCIO_ORIGENES)).optional(),
  operacion_ids: uuidArr,
  tipo_local_ids: uuidArr,
  licencia_ids: uuidArr,
  rating_min: z.number().int().min(1).max(5).optional(),
  provincia_ids: uuidArr,
  comarca_ids: uuidArr,
  ciudad_ids: uuidArr,
  distrito_ids: uuidArr,
  barrio_ids: uuidArr,
  cp: z.string().trim().max(10).optional(),
  superficie_min: z.number().nonnegative().optional(),
  superficie_max: z.number().nonnegative().optional(),
  aseos_min: z.number().int().nonnegative().optional(),
  parking_plazas_min: z.number().int().nonnegative().optional(),
  estado_inmueble: z.array(z.enum(ESTADOS_INMUEBLE)).optional(),
  contacto_ids: uuidArr,
  agencia_ids: uuidArr,
  uso_local_ids: uuidArr,
  precio_alquiler_min: z.number().nonnegative().optional(),
  precio_alquiler_max: z.number().nonnegative().optional(),
  precio_traspaso_min: z.number().nonnegative().optional(),
  precio_traspaso_max: z.number().nonnegative().optional(),
  precio_venta_min: z.number().nonnegative().optional(),
  precio_venta_max: z.number().nonnegative().optional(),
  rent_bruta_min: z.number().nonnegative().optional(),
  rent_bruta_max: z.number().nonnegative().optional(),
  only_ids: uuidArr,
  exclude_ids: uuidArr,
  // Página pública de agente: anuncios asignados a estos usuarios (se aplica también en público)
  asignado_ids: uuidArr,
  // Scope (DataScope): agencia impersonada por staff + filtro por asignado
  scope_agencia_id: z.string().uuid().nullable().optional(),
  sin_agencia: z.boolean().optional(),
  asignado: z.string().nullable().optional(),
  con_asignado: z.boolean().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(10000).optional(),
  offset: z.number().int().min(0).optional(),
  with_count: z.boolean().optional(),
});
type ListAnunciosData = z.infer<typeof listAnunciosInput>;

async function fetchAnunciosList(
  sb: ReturnType<typeof getSupabaseAdmin>,
  data: ListAnunciosData,
  forcePublic: boolean,
  pagination?: { offset: number; limit: number; withCount: boolean },
) {
  const wantCount = !!pagination && pagination.withCount;
  const publicCols =
    "id, referencia, descripcion, estado, visibilidad, operacion_id, tipo_local_id, ciudad_id, provincia_id, barrio_id, cp, precio_alquiler, precio_traspaso, precio_venta, rent_bruta, t_superficie, nivel_transito, t_esquinero, h_salida_humos, t_parking, t_parking_plazas, h_terraza, t_amueblado, agencia_id, vistas_total, impresiones_total, created_at, updated_at";
  const staffCols =
    publicCols +
    ", alias, origen, rating, direccion_completa, asignado_a, proceso" +
    ", valor_estimado, valor_estimado_min, valor_estimado_max, valorado_at" +
    ", ultima_estimacion_tipo, ultima_estimacion_version, ultima_estimacion_calibracion" +
    ", precio_validacion_estado";

  const baseCols = forcePublic ? publicCols : staffCols;
  // Cuando filtramos por uso_local_ids o contacto_ids, hacemos un inner-join embed
  // para filtrar a nivel SQL en vez de pre-cargar miles de anuncio_ids en una
  // cláusula IN (que reventaba el límite de longitud de URL de PostgREST y
  // hacía que filtros como "Bar" o "Restaurante" devolvieran 0 resultados).
  const embedUsos = !!data.uso_local_ids?.length;
  const embedContactos = !!data.contacto_ids?.length;
  const selectStr = baseCols
    + (embedUsos ? ", anuncio_usos_local!inner(uso_local_id)" : "")
    + (embedContactos ? ", anuncio_contactos!inner(contacto_id)" : "");
  let q = sb
    .from("anuncios")
    .select(selectStr, wantCount ? { count: "exact" } : undefined)
    .order("created_at", { ascending: false });

  if (embedUsos) q = q.in("anuncio_usos_local.uso_local_id", data.uso_local_ids as string[]);
  if (embedContactos) q = q.in("anuncio_contactos.contacto_id", data.contacto_ids as string[]);

  if (pagination) {
    const from = pagination.offset;
    const to = from + pagination.limit - 1;
    q = q.range(from, to);
  } else {
    q = q.limit(500);
  }



  if (forcePublic) {
    q = q.eq("estado", "publicado").eq("visibilidad", "publico");
    // Excluir anuncios de agencias bloqueadas
    const { data: blocked } = await sb
      .from("agencias")
      .select("id")
      .eq("estado", "bloqueada");
    const blockedIds = (blocked ?? []).map((b: { id: string }) => b.id);
    if (blockedIds.length) {
      // Incluir explícitamente los anuncios con agencia_id IS NULL:
      // en SQL, `agencia_id NOT IN (...)` devuelve UNKNOWN cuando la columna
      // es NULL y esas filas quedan fuera. Sin este OR perdíamos ~2000
      // anuncios publicados que no tienen agencia asignada.
      q = q.or(
        `agencia_id.is.null,agencia_id.not.in.(${blockedIds.join(",")})`,
      );
    }
  }

  if (data.only_ids?.length) q = q.in("id", data.only_ids);
  if (data.exclude_ids?.length) {
    q = q.not("id", "in", `(${data.exclude_ids.join(",")})`);
  }

  if (!forcePublic && data.estado) q = q.eq("estado", data.estado);
  if (data.operacionId) q = q.eq("operacion_id", data.operacionId);
  if (data.ciudadId) q = q.eq("ciudad_id", data.ciudadId);
  if (data.q) {
    if (forcePublic) q = q.ilike("referencia", `%${data.q}%`);
    else for (const t of searchTerms(data.q)) q = q.ilike("busqueda", `%${t}%`);
  }

  if (!forcePublic && data.estados?.length) q = q.in("estado", data.estados);
  if (!forcePublic && data.visibilidades?.length) q = q.in("visibilidad", data.visibilidades);
  if (!forcePublic && data.origenes?.length) q = q.in("origen", data.origenes);
  if (data.operacion_ids?.length) q = q.in("operacion_id", data.operacion_ids);
  if (data.tipo_local_ids?.length) q = q.in("tipo_local_id", data.tipo_local_ids);
  if (data.licencia_ids?.length) q = q.in("licencia_id", data.licencia_ids);
  if (!forcePublic && data.rating_min != null) q = q.gte("rating", data.rating_min);

  if (data.provincia_ids?.length) q = q.in("provincia_id", data.provincia_ids);
  if (data.comarca_ids?.length) {
    // Una comarca agrupa ciudades: se filtra por sus ciudades. Si además hay
    // ciudades elegidas, ambos filtros se combinan (AND).
    const { data: ciuds, error: ciuErr } = await sb
      .from("ciudades")
      .select("id")
      .in("comarca_id", data.comarca_ids)
      .range(0, 9999);
    if (ciuErr) throw new Error(ciuErr.message);
    const ids = (ciuds ?? []).map((c: { id: string }) => c.id);
    if (!ids.length) return pagination ? { items: [] as any[], total: 0 } : [];
    q = q.in("ciudad_id", ids);
  }
  if (data.ciudad_ids?.length) q = q.in("ciudad_id", data.ciudad_ids);
  if (data.distrito_ids?.length) q = q.in("distrito_id", data.distrito_ids);
  if (data.barrio_ids?.length) q = q.in("barrio_id", data.barrio_ids);
  if (data.cp) q = q.ilike("cp", `${data.cp}%`);

  if (data.superficie_min != null) q = q.gte("t_superficie", data.superficie_min);
  if (data.superficie_max != null) q = q.lte("t_superficie", data.superficie_max);
  if (data.aseos_min != null) q = q.gte("t_num_aseos", data.aseos_min);
  if (data.parking_plazas_min != null) q = q.gte("t_parking_plazas", data.parking_plazas_min);
  if (data.estado_inmueble?.length) q = q.in("estado_inmueble", data.estado_inmueble);

  if (data.agencia_ids?.length) q = q.in("agencia_id", data.agencia_ids);
  if (data.asignado_ids?.length) q = q.in("asignado_a", data.asignado_ids);

  if (!forcePublic) {
    if (data.sin_agencia) q = q.is("agencia_id", null);
    else if (data.scope_agencia_id) q = q.eq("agencia_id", data.scope_agencia_id);
    q = applyAsignadoFilter(q, data.asignado);
    if (data.con_asignado) q = q.not("asignado_a", "is", null);
  }

  if (data.precio_alquiler_min != null) q = q.gte("precio_alquiler", data.precio_alquiler_min);
  if (data.precio_alquiler_max != null) q = q.lte("precio_alquiler", data.precio_alquiler_max);
  if (data.precio_traspaso_min != null) q = q.gte("precio_traspaso", data.precio_traspaso_min);
  if (data.precio_traspaso_max != null) q = q.lte("precio_traspaso", data.precio_traspaso_max);
  if (data.precio_venta_min != null) q = q.gte("precio_venta", data.precio_venta_min);
  if (data.precio_venta_max != null) q = q.lte("precio_venta", data.precio_venta_max);
  if (data.rent_bruta_min != null) q = q.gte("rent_bruta", data.rent_bruta_min);
  if (data.rent_bruta_max != null) q = q.lte("rent_bruta", data.rent_bruta_max);

  const emptyResult = () => (pagination ? { items: [] as any[], total: 0 } : []);

  // contacto_ids y uso_local_ids ya se aplican vía embed !inner arriba.


  const { data: rows, error, count } = await q;
  if (error) throw new Error(error.message);

  type Row = {
    id: string;
    referencia: string;
    alias: string | null;
    direccion_completa: string | null;
    descripcion: string | null;
    estado: string;
    visibilidad: string;
    origen: string;
    operacion_id: string | null;
    tipo_local_id: string | null;
    ciudad_id: string | null;
    provincia_id: string | null;
    barrio_id: string | null;
    cp: string | null;
    precio_alquiler: number | null;
    precio_traspaso: number | null;
    precio_venta: number | null;
    rent_bruta: number | null;
    t_superficie: number | null;
    nivel_transito: string | null;
    t_esquinero: boolean | null;
    h_salida_humos: boolean | null;
    t_parking: boolean | null;
    t_parking_plazas: number | null;
    h_terraza: boolean | null;
    t_amueblado: boolean | null;
    agencia_id: string | null;
    asignado_a?: string | null;
    proceso?: string | null;
    vistas_total: number | null;
    impresiones_total: number | null;
    created_at: string;
    updated_at: string;
  };
  const list = (rows ?? []) as unknown as Row[];
  if (list.length === 0) return emptyResult();


  const ids = list.map((r) => r.id);
  const opIds = Array.from(new Set(list.map((r) => r.operacion_id).filter(Boolean)));
  const ciudadIds = Array.from(new Set(list.map((r) => r.ciudad_id).filter(Boolean)));
  const provIds = Array.from(new Set(list.map((r) => r.provincia_id).filter(Boolean)));
  const barrioIds = Array.from(new Set(list.map((r) => r.barrio_id).filter(Boolean)));
  const tipoIds = Array.from(new Set(list.map((r) => r.tipo_local_id).filter(Boolean)));
  const agenciaIds = Array.from(new Set(list.map((r) => r.agencia_id).filter(Boolean)));

  const chunk = <T,>(arr: T[], size: number): T[][] => {
    const out: T[][] = [];
    for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
    return out;
  };

  const mediaChunks = await Promise.all(
    chunk(ids, 100).map((c) =>
      sb
        .from("media")
        .select("anuncio_id, storage_path, orden")
        .eq("tipo", "imagen")
        .in("anuncio_id", c)
        .order("orden", { ascending: true }),
    ),
  );
  const mediaRows = mediaChunks.flatMap((r) => r.data ?? []);

  const [
    { data: opsRows },
    { data: ciudadesRows },
    { data: provinciasRows },
    { data: barriosRows },
    { data: tiposRows },
    { data: usosRows },
    { data: agenciasRows },
    { data: anuncianteRows },
  ] = await Promise.all([
    filasPorId(sb, "operaciones", "id, nombre", opIds),
    filasPorId(sb, "ciudades", "id, nombre", ciudadIds),
    filasPorId(sb, "provincias", "id, nombre", provIds),
    filasPorId(sb, "barrios", "id, nombre", barrioIds),
    filasPorId(sb, "tipos_local", "id, nombre", tipoIds),
    sb.from("anuncio_usos_local").select("anuncio_id, usos_local(nombre)").in("anuncio_id", ids),
    filasPorId(sb, "agencias", "id, nombre, logo_url", agenciaIds),
    !forcePublic && ids.length
      ? sb
          .from("anuncio_contactos")
          .select("anuncio_id, contacto_id, rol, contactos(nombre, apellidos, email)")
          .in("anuncio_id", ids)
          .eq("rol", "anunciante")
      : Promise.resolve({ data: [] }),
  ]);

  const coverByAnuncio = new Map<string, string>();
  for (const m of mediaRows as Array<{ anuncio_id: string; storage_path: string }>) {
    if (!coverByAnuncio.has(m.anuncio_id)) {
      const { data: pub } = sb.storage.from("media").getPublicUrl(m.storage_path);
      coverByAnuncio.set(m.anuncio_id, pub.publicUrl);
    }
  }


  const opById = new Map((opsRows ?? []).map((o: any) => [o.id, o.nombre]));
  const ciudadById = new Map((ciudadesRows ?? []).map((c: any) => [c.id, c.nombre]));
  const provById = new Map((provinciasRows ?? []).map((p: any) => [p.id, p.nombre]));
  const barrioById = new Map((barriosRows ?? []).map((b: any) => [b.id, b.nombre]));
  const tipoById = new Map((tiposRows ?? []).map((t: any) => [t.id, t.nombre]));
  const agenciaById = new Map(
    (agenciasRows ?? []).map((a: any) => [a.id, { nombre: a.nombre as string, logo_url: (a.logo_url as string | null) ?? null }]),
  );

  const usosByAnuncio = new Map<string, string[]>();
  for (const u of (usosRows ?? []) as any[]) {
    const anuncioId = u.anuncio_id as string;
    const nombre = u.usos_local?.nombre ?? "";
    if (!nombre) continue;
    const arr = usosByAnuncio.get(anuncioId) ?? [];
    arr.push(nombre);
    usosByAnuncio.set(anuncioId, arr);
  }

  const anuncianteByAnuncio = new Map<string, { nombre: string | null; email: string | null }>();
  for (const ac of (anuncianteRows ?? []) as any[]) {
    const anuncioId = ac.anuncio_id as string;
    const c = ac.contactos;
    const nombre = [c?.nombre, c?.apellidos].filter(Boolean).join(" ").trim() || null;
    anuncianteByAnuncio.set(anuncioId, { nombre, email: c?.email ?? null });
  }

  const items = list.map((r) => {
    const ag = r.agencia_id ? agenciaById.get(r.agencia_id) ?? null : null;
    return {
      ...r,
      cover_url: coverByAnuncio.get(r.id) ?? null,
      operacion: opById.get(r.operacion_id ?? "") ?? null,
      ciudad: ciudadById.get(r.ciudad_id ?? "") ?? null,
      provincia: provById.get(r.provincia_id ?? "") ?? null,
      barrio: barrioById.get(r.barrio_id ?? "") ?? null,
      tipo_local: tipoById.get(r.tipo_local_id ?? "") ?? null,
      usos_local: usosByAnuncio.get(r.id) ?? [],
      agencia_nombre: ag?.nombre ?? null,
      agencia_logo_url: ag?.logo_url ?? null,
      direccion_completa: forcePublic ? null : r.direccion_completa,
      anunciante: forcePublic ? null : (anuncianteByAnuncio.get(r.id) ?? null),
    };
  });
  return pagination ? { items, total: wantCount ? (count ?? items.length) : items.length } : items;
}


export const listAnuncios = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => listAnunciosInput.parse(d ?? {}))
  .handler(async ({ data: input }) => {
    const sb = getSupabaseAdmin();
    const { resolveAmbito } = await import("@/lib/scope-permisos.server");
    const ambito = await resolveAmbito({
      agenciaVista: input.scope_agencia_id ?? null,
      asignado: input.asignado ?? null,
      verEquipo: input.ver_equipo,
    });
    // En modo equipo, lo que no lleva el propio editor se marca como de un
    // compañero: la interfaz lo abre en solo lectura.
    const marcarCompaneros = <T,>(res: T): T => {
      if (ambito.esAmplio || !input.ver_equipo) return res;
      const marcar = (rows: Array<{ asignado_a?: string | null }>) =>
        rows.map((r) => ({ ...r, de_companero: r.asignado_a !== ambito.userId }));
      if (Array.isArray(res)) return marcar(res) as T;
      const r = res as { items?: Array<{ asignado_a?: string | null }> };
      return (r.items ? { ...r, items: marcar(r.items) } : res) as T;
    };
    const data = {
      ...input,
      asignado: ambito.asignadoEfectivo,
      // Un miembro de agencia nunca puede salirse de sus agencias, aunque el
      // cliente no mande el ámbito o lo manipule por URL.
      ...(ambito.isStaff
        ? {}
        : {
            sin_agencia: false,
            scope_agencia_id: ambito.agenciaEfectiva ?? undefined,
            agencia_ids: ambito.agenciaEfectiva ? undefined : ambito.agenciaIds,
          }),
    };
    if (!ambito.isStaff && !ambito.agenciaIds.length) {
      return input.with_count || input.limit != null || input.page != null
        ? { items: [], total: 0 }
        : [];
    }

    if (data.with_count || data.limit != null || data.offset != null || data.page != null) {
      const limit = data.limit ?? 5000;
      const offset = data.offset != null ? data.offset : ((data.page ?? 1) - 1) * limit;
      const withCount = data.with_count ?? true;
      return marcarCompaneros(await fetchAnunciosList(sb, data, false, { offset, limit, withCount }));
    }
    return marcarCompaneros(await fetchAnunciosList(sb, data, false));
  });


export const listAnunciosPublicos = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => listAnunciosInput.parse(d ?? {}))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const limit = data.limit ?? 30;
    const offset = data.offset != null ? data.offset : ((data.page ?? 1) - 1) * limit;
    const withCount = data.with_count ?? true;
    return fetchAnunciosList(sb, data, true, { offset, limit, withCount }) as Promise<{
      items: any[];
      total: number;
    }>;
  });



export const listAnunciosVip = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => listAnunciosInput.parse(d ?? {}))
  .handler(async ({ data, context }) => {
    const sb = getSupabaseAdmin();
    await requireVipAccess();
    const limit = data.limit ?? 30;
    const offset = data.offset != null ? data.offset : ((data.page ?? 1) - 1) * limit;
    const withCount = data.with_count ?? true;
    // Forzar estado=publicado y visibilidad=off_market
    const vipData = {
      ...data,
      estados: ["publicado" as const],
      visibilidades: ["off_market" as const],
    };
    const result = await fetchAnunciosList(sb, vipData, false, { offset, limit, withCount }) as {
      items: any[];
      total: number;
    };
    console.log("[listAnunciosVip] user", context.userId, "returned", result.items.length, "total", result.total);
    // En el portal VIP no revelamos quién anuncia cada local.
    result.items = result.items.map((it) => ({
      ...it,
      agencia_id: null,
      agencia_nombre: null,
      agencia_logo_url: null,
    }));
    return result;
  });

// Modo demo: no requiere autenticación ni rol VIP.
// Devuelve únicamente anuncios con estado='demo' (cualquier visibilidad).
export const listAnunciosDemo = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => listAnunciosInput.parse(d ?? {}))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const limit = data.limit ?? 30;
    const offset = data.offset != null ? data.offset : ((data.page ?? 1) - 1) * limit;
    const withCount = data.with_count ?? true;
    const demoData = {
      ...data,
      estados: ["demo" as const],
      visibilidades: undefined,
    };
    return fetchAnunciosList(sb, demoData, false, { offset, limit, withCount }) as Promise<{
      items: any[];
      total: number;
    }>;
  });


// ───────── Get ─────────
async function assertCanAccessAnuncio(anuncioId: string): Promise<void> {
  const sb = getSupabaseAdmin();
  const userId = await resolveCurrentUserId();
  if (!userId) throw new Error("Unauthorized");
  if (await userIsStaff(userId)) return;
  const { data: a } = await sb
    .from("anuncios")
    .select("created_by, agencia_id")
    .eq("id", anuncioId)
    .maybeSingle();
  if (!a) throw new Error("Anuncio no encontrado");
  if (a.created_by === userId) return;
  if (a.agencia_id) {
    const { data: m } = await sb
      .from("agencia_miembros")
      .select("user_id")
      .eq("agencia_id", a.agencia_id)
      .eq("user_id", userId)
      .maybeSingle();
    if (m) return;
  }
  // Su propia ficha figura como anunciante o propietario (p. ej. anuncios
  // importados de portales, que no tienen creador). Coincide con lo que lista
  // "Mis anuncios" en /mybizaclia.
  const { data: propios } = await sb
    .from("contactos")
    .select("id, anuncio_contactos!inner(anuncio_id)")
    .eq("user_id", userId)
    .eq("anuncio_contactos.anuncio_id", anuncioId)
    .in("anuncio_contactos.rol", ["anunciante", "propietario"])
    .limit(1);
  if (propios?.length) return;
  throw new Error("No tienes permiso para acceder a este anuncio");
}

/** Valoración enlazada vigente que viaja al cliente junto al anuncio. */
type ValoracionEnlazada = {
  id: string;
  created_at: string;
  version_modelo: string | null;
  /** JSON completo del resultado del estimador (tipo_resultado real incluido). */
  resultado: Json | null;
};

export const getAnuncio = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await assertCanAccessAnuncio(data.id);
    const sb = getSupabaseAdmin();
    const [anuncio, contactos, usos] = await Promise.all([
      sb.from("anuncios").select("*").eq("id", data.id).single(),
      sb
        .from("anuncio_contactos")
        .select("id, contacto_id, rol, rol_custom, contactos(nombre, apellidos, email)")
        .eq("anuncio_id", data.id),
      sb
        .from("anuncio_usos_local")
        .select("uso_local_id")
        .eq("anuncio_id", data.id),
    ]);
    if (anuncio.error) throw new Error(anuncio.error.message);
    if (contactos.error) throw new Error(contactos.error.message);
    if (usos.error) throw new Error(usos.error.message);
    // Valoración enlazada vigente (JSON completo). La tarjeta necesita
    // `tipo_resultado` real: las columnas desnormalizadas del anuncio sólo
    // llevan números cuando el tipo es `valoracion`.
    const ultimaValoracionId = (anuncio.data as { ultima_valoracion_id?: string | null })
      ?.ultima_valoracion_id ?? null;
    let ultimaValoracion: ValoracionEnlazada | null = null;
    if (ultimaValoracionId) {
      const { data: v } = await sb
        .from("valoraciones")
        .select("id, created_at, version_modelo, resultado")
        .eq("id", ultimaValoracionId)
        .maybeSingle();
      ultimaValoracion = (v as ValoracionEnlazada | null) ?? null;
    }
    return {
      anuncio: anuncio.data,
      ultima_valoracion: ultimaValoracion,
      contactos: contactos.data ?? [],
      uso_local_ids: (usos.data ?? []).map((u: { uso_local_id: string }) => u.uso_local_id),
    };
  });



// ───────── Get público (página de detalle del local) ─────────
const publicAnuncioDetailCols = [
  "id",
  "referencia",
  "referencia_ext",
  "slug",
  "descripcion",
  "estado",
  "visibilidad",
  "direccion_visible",
  "agencia_id",
  "operacion_id",
  "tipo_local_id",
  "licencia_id",
  "provincia_id",
  "ciudad_id",
  "distrito_id",
  "barrio_id",
  "cp",
  "precio_alquiler",
  "precio_traspaso",
  "precio_venta",
  "rent_bruta",
  "rent_neta",
  "t_superficie",
  "t_num_aseos",
  "t_parking_plazas",
  "nivel_transito",
  "estado_inmueble",
  "e_consumo",
  "e_emisiones",
  "a_pie_de_calle",
  "t_esquinero",
  "t_mucha_luz",
  "t_amueblado",
  "t_aire_acondicionado",
  "t_cocina",
  "t_agua",
  "t_alarma",
  "t_salida_emergencia",
  "t_acceso_movilidad_reducida",
  "t_parking",
  "h_aforo",
  "h_salida_humos",
  "h_camara_frigorifica",
  "h_obrador",
  "h_terraza",
  "n_muelle_carga",
  "n_vado",
  "distribucion",
  "vistas_total",
  "impresiones_total",
  "created_at",
  "updated_at",
].join(", ");

/** Campos internos que no ven los usuarios ajenos al anuncio. */
const ANUNCIO_CAMPOS_INTERNOS = [
  "alias",
  "calle",
  "calle_id",
  "numero",
  "numero_norm",
  "rating",
  "origen",
  "idealista_id",
  "idealista_sync",
  "url_original",
  "asignado_a",
  "busqueda",
] as const;
/** Ubicación exacta: solo se envía si el anuncio permite mostrar la dirección. */
const ANUNCIO_CAMPOS_DIRECCION = ["direccion_completa", "lat", "lng"] as const;

async function puedeVerDatosInternosAnuncio(
  userId: string,
  anuncio: { created_by?: string | null; agencia_id?: string | null },
): Promise<boolean> {
  if (anuncio.created_by === userId) return true;
  if (await userIsStaff(userId)) return true;
  if (!anuncio.agencia_id) return false;
  return (await currentUserAgenciaIds(userId)).includes(anuncio.agencia_id);
}

export const getAnuncioPublico = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const userId = await resolveCurrentUserId();
    const { data: a, error } = await sb
      .from("anuncios")
      .select(userId ? "*" : publicAnuncioDetailCols)
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!a) {
      const { notFound } = await import("@tanstack/react-router");
      throw notFound();
    }
    const anuncioRow = a as any;

    // Comprobar si la agencia está bloqueada
    let agenciaBloqueada = false;
    if (anuncioRow.agencia_id) {
      const { data: ag } = await sb
        .from("agencias")
        .select("estado")
        .eq("id", anuncioRow.agencia_id)
        .maybeSingle();
      agenciaBloqueada = (ag as any)?.estado === "bloqueada";
    }

    const esPublico =
      anuncioRow.estado === "publicado" &&
      anuncioRow.visibilidad === "publico" &&
      !agenciaBloqueada;
    if (!esPublico) {
      let permitido = false;
      if (userId) {
        if (anuncioRow.created_by === userId) {
          permitido = true;
        } else if (anuncioRow.agencia_id) {
          const { data: miembro } = await sb
            .from("agencia_miembros")
            .select("user_id")
            .eq("agencia_id", anuncioRow.agencia_id)
            .eq("user_id", userId)
            .maybeSingle();
          if (miembro) permitido = true;
        }
        if (!permitido) {
          const allowedRoles = ["agente", "customer_service", "admin"];
          const { data: staff } = await sb
            .from("user_roles")
            .select("role")
            .eq("user_id", userId)
            .in("role", allowedRoles);
          if (staff && staff.length > 0) permitido = true;
          if (!permitido && anuncioRow.visibilidad === "off_market" && anuncioRow.estado === "publicado" && !agenciaBloqueada) {
            permitido = (await getVipAccess(sb, userId)).vip_activo;
          }
        }
      }
      if (!permitido) {
        const { notFound } = await import("@tanstack/react-router");
        throw notFound();
      }
    }


    const [
      mediaRes,
      usosRes,
      opRes,
      tipoRes,
      ciudadRes,
      provRes,
      barrioRes,
      distritoRes,
      agenciaRes,
      licenciaRes,
    ] = await Promise.all([
      sb
        .from("media")
        .select("id, tipo, storage_path, watermark_path, stream_video_id, stream_status, titulo, orden, width, height")
        .eq("anuncio_id", anuncioRow.id)
        .order("orden", { ascending: true })
        .order("created_at", { ascending: true }),
      sb
        .from("anuncio_usos_local")
        .select("usos_local(id, nombre, slug)")
        .eq("anuncio_id", anuncioRow.id),
      anuncioRow.operacion_id
        ? sb.from("operaciones").select("id, nombre, slug").eq("id", anuncioRow.operacion_id).maybeSingle()
        : Promise.resolve({ data: null }),
      anuncioRow.tipo_local_id
        ? sb.from("tipos_local").select("id, nombre, slug").eq("id", anuncioRow.tipo_local_id).maybeSingle()
        : Promise.resolve({ data: null }),
      anuncioRow.ciudad_id
        ? sb.from("ciudades").select("id, nombre, slug").eq("id", anuncioRow.ciudad_id).maybeSingle()
        : Promise.resolve({ data: null }),
      anuncioRow.provincia_id
        ? sb.from("provincias").select("id, nombre, slug").eq("id", anuncioRow.provincia_id).maybeSingle()
        : Promise.resolve({ data: null }),
      anuncioRow.barrio_id
        ? sb.from("barrios").select("id, nombre, slug").eq("id", anuncioRow.barrio_id).maybeSingle()
        : Promise.resolve({ data: null }),
      anuncioRow.distrito_id
        ? sb.from("distritos").select("id, nombre, slug").eq("id", anuncioRow.distrito_id).maybeSingle()
        : Promise.resolve({ data: null }),
      anuncioRow.agencia_id
        ? sb
            .from("agencias")
            .select("id, slug, nombre, logo_url, descripcion_publica, url_web, region")
            .eq("id", anuncioRow.agencia_id)
            .maybeSingle()
        : Promise.resolve({ data: null }),
      anuncioRow.licencia_id
        ? sb.from("licencias").select("id, nombre, slug").eq("id", anuncioRow.licencia_id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);


    const mediaRows = (mediaRes.data ?? []) as any[];

    // Vídeos de Stream cuyo estado guardado aún no es "ready": el estado solo
    // se refrescaba desde el panel de admin, así que lo comprobamos aquí para
    // que un vídeo ya procesado aparezca en la ficha pública.
    const pendingVideos = mediaRows.filter(
      (m) => m.tipo === "video" && m.stream_video_id && m.stream_status !== "ready" && m.stream_status !== "not_found",
    );
    if (pendingVideos.length > 0) {
      const { getStreamVideoInfo } = await import("./media.server");
      await Promise.all(
        pendingVideos.map(async (m) => {
          try {
            const info = await getStreamVideoInfo(m.stream_video_id as string);
            const newStatus = info.found ? (info.readyToStream ? "ready" : info.state ?? "processing") : "not_found";
            if (newStatus !== m.stream_status) {
              m.stream_status = newStatus;
              await sb.from("media").update({ stream_status: newStatus }).eq("id", m.id);
            }
          } catch (e) {
            console.error("[getAnuncioPublico] refresh stream status", e);
          }
        }),
      );
    }

    const media = mediaRows.map((m: any) => {
      let url: string | null = null;
      if (m.tipo === "imagen" && m.storage_path) {
        const { data: pub } = sb.storage.from("media").getPublicUrl(m.watermark_path ?? m.storage_path);
        url = pub.publicUrl;
      } else if (m.tipo === "video" && !m.stream_video_id && m.storage_path) {
        // Vídeos antiguos subidos directamente al bucket (sin Stream).
        const { data: pub } = sb.storage.from("media").getPublicUrl(m.storage_path);
        url = pub.publicUrl;
      }
      return {
        id: m.id,
        tipo: m.tipo as "imagen" | "video",
        url,
        titulo: m.titulo as string | null,
        stream_video_id: m.stream_video_id as string | null,
        stream_status: (m.stream_status as string | null) ?? null,
        width: m.width as number | null,
        height: m.height as number | null,
      };
    });

    const usos = ((usosRes.data ?? []) as any[])
      .map((u) => u.usos_local)
      .filter(Boolean) as Array<{ id: string; nombre: string; slug: string | null }>;

    // Sanitizamos: no devolvemos created_by ni notas_internas al público, y a
    // quien no es staff, creador ni de la agencia dueña tampoco los datos
    // internos ni la dirección exacta (salvo que el anuncio la muestre).
    const { created_by: _cb, notas_internas: _ni, ...safeAnuncio } = anuncioRow;
    const veDatosInternos = userId ? await puedeVerDatosInternosAnuncio(userId, anuncioRow) : false;
    // En los anuncios off-market (portal VIP) no revelamos quién los anuncia
    // salvo a staff, creador o miembros de la agencia dueña.
    const ocultarAgencia = anuncioRow.visibilidad === "off_market" && !veDatosInternos;
    if (userId && !veDatosInternos) {
      for (const k of ANUNCIO_CAMPOS_INTERNOS) delete (safeAnuncio as Record<string, unknown>)[k];
      if (anuncioRow.direccion_visible === false) {
        for (const k of ANUNCIO_CAMPOS_DIRECCION) delete (safeAnuncio as Record<string, unknown>)[k];
      }
    }

    return {
      anuncio: safeAnuncio,
      media,
      usos,
      operacion: (opRes as any).data ?? null,
      tipo_local: (tipoRes as any).data ?? null,
      ciudad: (ciudadRes as any).data ?? null,
      provincia: (provRes as any).data ?? null,
      barrio: (barrioRes as any).data ?? null,
      distrito: (distritoRes as any).data ?? null,
      agencia: ocultarAgencia ? null : ((agenciaRes as any).data ?? null),
      licencia: (licenciaRes as any).data ?? null,
    };
  });

// ───────── SEO bundle para /local/:id (no lanza notFound para inactivos) ─────────
export const getLocalSeoBundle = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const seoCols = [
      "id","referencia","descripcion","estado","visibilidad","agencia_id",
      "operacion_id","tipo_local_id","provincia_id","ciudad_id","distrito_id","barrio_id",
      "precio_alquiler","precio_traspaso","precio_venta","rent_bruta","t_superficie",
      "h_salida_humos","h_terraza","licencia_id","facturacion_anual",
      "created_at",
    ].join(", ");
    const { data: a } = await sb
      .from("anuncios")
      .select(seoCols)
      .eq("id", data.id)
      .maybeSingle();
    if (!a) return { status: "missing" as const };
    const row = a as any;

    let agenciaEstado: string | null = null;
    if (row.agencia_id) {
      const { data: ag } = await sb
        .from("agencias")
        .select("estado")
        .eq("id", row.agencia_id)
        .maybeSingle();
      agenciaEstado = (ag as any)?.estado ?? null;
    }
    const activo =
      row.estado === "publicado" &&
      row.visibilidad === "publico" &&
      agenciaEstado !== "bloqueada";

    const [opRes, tipoRes, provRes, ciudadRes, barrioRes, licRes, usoRes] = await Promise.all([
      row.operacion_id
        ? sb.from("operaciones").select("id, nombre, slug").eq("id", row.operacion_id).maybeSingle()
        : Promise.resolve({ data: null }),
      row.tipo_local_id
        ? sb.from("tipos_local").select("id, nombre, slug").eq("id", row.tipo_local_id).maybeSingle()
        : Promise.resolve({ data: null }),
      row.provincia_id
        ? sb.from("provincias").select("id, nombre, slug").eq("id", row.provincia_id).maybeSingle()
        : Promise.resolve({ data: null }),
      row.ciudad_id
        ? sb.from("ciudades").select("id, nombre, slug").eq("id", row.ciudad_id).maybeSingle()
        : Promise.resolve({ data: null }),
      row.barrio_id
        ? sb.from("barrios").select("id, nombre, slug").eq("id", row.barrio_id).maybeSingle()
        : Promise.resolve({ data: null }),
      row.licencia_id
        ? sb.from("licencias").select("id, nombre").eq("id", row.licencia_id).maybeSingle()
        : Promise.resolve({ data: null }),
      sb.from("anuncio_usos_local")
        .select("usos_local(id, nombre, slug)")
        .eq("anuncio_id", row.id)
        .limit(1),
    ]);

    const operacion = (opRes as any).data ?? null;
    const tipo_local = (tipoRes as any).data ?? null;
    const provincia = (provRes as any).data ?? null;
    const ciudad = (ciudadRes as any).data ?? null;
    const barrio = (barrioRes as any).data ?? null;
    const licencia = (licRes as any).data ?? null;
    const usoRow = ((usoRes as any).data ?? [])[0]?.usos_local ?? null;

    // Primera imagen
    const { data: mediaRow } = await sb
      .from("media")
      .select("storage_path, watermark_path, titulo")
      .eq("anuncio_id", row.id)
      .eq("tipo", "imagen")
      .order("orden", { ascending: true })
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    let coverUrl: string | null = null;
    if (mediaRow?.storage_path) {
      const { data: pub } = sb.storage
        .from("media")
        .getPublicUrl((mediaRow as any).watermark_path ?? mediaRow.storage_path);
      coverUrl = pub.publicUrl ?? null;
    }

    // Anuncios relacionados (solo si el actual está activo)
    let related: Array<{
      id: string;
      referencia: string | null;
      operacion_slug: string | null;
      tipo_nombre: string | null;
      uso_nombre: string | null;
      ciudad_nombre: string | null;
      barrio_nombre: string | null;
      precio: number | null;
    }> = [];
    if (activo) {
      // Excluir agencias bloqueadas
      const { data: blocked } = await sb
        .from("agencias")
        .select("id")
        .eq("estado", "bloqueada");
      const blockedIds = (blocked ?? []).map((b: { id: string }) => b.id);

      let rq = sb
        .from("anuncios")
        .select("id, referencia, operacion_id, tipo_local_id, ciudad_id, barrio_id, precio_alquiler, precio_traspaso, precio_venta")
        .eq("estado", "publicado")
        .eq("visibilidad", "publico")
        .neq("id", row.id)
        .order("created_at", { ascending: false })
        .limit(6);
      if (row.operacion_id) rq = rq.eq("operacion_id", row.operacion_id);
      if (row.ciudad_id) rq = rq.eq("ciudad_id", row.ciudad_id);
      if (blockedIds.length) {
        rq = rq.or(`agencia_id.is.null,agencia_id.not.in.(${blockedIds.join(",")})`);
      }
      let { data: rel } = await rq;
      if (!rel || rel.length === 0) {
        // Fallback: por tipo_local + ciudad
        let rq2 = sb
          .from("anuncios")
          .select("id, referencia, operacion_id, tipo_local_id, ciudad_id, barrio_id, precio_alquiler, precio_traspaso, precio_venta")
          .eq("estado", "publicado")
          .eq("visibilidad", "publico")
          .neq("id", row.id)
          .order("created_at", { ascending: false })
          .limit(6);
        if (row.tipo_local_id) rq2 = rq2.eq("tipo_local_id", row.tipo_local_id);
        if (row.ciudad_id) rq2 = rq2.eq("ciudad_id", row.ciudad_id);
        if (blockedIds.length) {
          rq2 = rq2.or(`agencia_id.is.null,agencia_id.not.in.(${blockedIds.join(",")})`);
        }
        const r2 = await rq2;
        rel = r2.data ?? [];
      }

      const relRows = rel ?? [];
      // Resolver nombres de ciudad/tipo/uso/op para cada relacionado en batch
      const relOpIds = Array.from(new Set(relRows.map((r: any) => r.operacion_id).filter(Boolean)));
      const relTipoIds = Array.from(new Set(relRows.map((r: any) => r.tipo_local_id).filter(Boolean)));
      const relCiudadIds = Array.from(new Set(relRows.map((r: any) => r.ciudad_id).filter(Boolean)));
      const relBarrioIds = Array.from(new Set(relRows.map((r: any) => r.barrio_id).filter(Boolean)));
      const [opMap, tipoMap, ciudadMap, barrioMap, usosMap] = await Promise.all([
        relOpIds.length
          ? filasPorId(sb, "operaciones", "id, nombre, slug", relOpIds as string[])
          : Promise.resolve({ data: [] }),
        relTipoIds.length
          ? filasPorId(sb, "tipos_local", "id, nombre", relTipoIds as string[])
          : Promise.resolve({ data: [] }),
        relCiudadIds.length
          ? filasPorId(sb, "ciudades", "id, nombre", relCiudadIds as string[])
          : Promise.resolve({ data: [] }),
        relBarrioIds.length
          ? filasPorId(sb, "barrios", "id, nombre", relBarrioIds as string[])
          : Promise.resolve({ data: [] }),
        relRows.length
          ? sb.from("anuncio_usos_local").select("anuncio_id, usos_local(nombre)").in("anuncio_id", relRows.map((r: any) => r.id))
          : Promise.resolve({ data: [] }),
      ]);
      const ops = new Map((((opMap as any).data) ?? []).map((x: any) => [x.id, x]));
      const tipos = new Map((((tipoMap as any).data) ?? []).map((x: any) => [x.id, x.nombre]));
      const ciudades = new Map((((ciudadMap as any).data) ?? []).map((x: any) => [x.id, x.nombre]));
      const barrios = new Map((((barrioMap as any).data) ?? []).map((x: any) => [x.id, x.nombre]));
      const usoByAnuncio = new Map<string, string>();
      for (const u of ((usosMap as any).data ?? []) as any[]) {
        if (u.usos_local?.nombre && !usoByAnuncio.has(u.anuncio_id))
          usoByAnuncio.set(u.anuncio_id, u.usos_local.nombre);
      }
      related = relRows.map((r: any) => {
        const op: any = r.operacion_id ? ops.get(r.operacion_id) : null;
        const precio =
          op?.slug === "traspaso" ? r.precio_traspaso :
          op?.slug === "alquiler" ? r.precio_alquiler :
          op?.slug === "venta" || op?.slug === "rentabilidad" ? r.precio_venta :
          r.precio_traspaso ?? r.precio_venta ?? r.precio_alquiler ?? null;
        return {
          id: r.id,
          referencia: r.referencia ?? null,
          operacion_slug: op?.slug ?? null,
          tipo_nombre: r.tipo_local_id ? (tipos.get(r.tipo_local_id) as string ?? null) : null,
          uso_nombre: usoByAnuncio.get(r.id) ?? null,
          ciudad_nombre: r.ciudad_id ? (ciudades.get(r.ciudad_id) as string ?? null) : null,
          barrio_nombre: r.barrio_id ? (barrios.get(r.barrio_id) as string ?? null) : null,
          precio,
        };
      });
    }

    return {
      status: activo ? ("active" as const) : ("inactive" as const),
      anuncio: {
        id: row.id,
        referencia: row.referencia,
        descripcion: row.descripcion,
        precio_alquiler: row.precio_alquiler,
        precio_traspaso: row.precio_traspaso,
        precio_venta: row.precio_venta,
        rent_bruta: row.rent_bruta,
        t_superficie: row.t_superficie,
        h_salida_humos: !!row.h_salida_humos,
        h_terraza: !!row.h_terraza,
        facturacion_anual: row.facturacion_anual,
        created_at: row.created_at,
      },
      operacion,
      tipo_local,
      uso: usoRow,
      provincia,
      ciudad,
      barrio,
      licencia,
      coverUrl,
      coverAlt: (mediaRow as any)?.titulo ?? null,
      related,
    };
  });



// ───────── Create ─────────
/** Un usuario que no es staff solo puede asignar anuncios a agencias de las que es miembro. */
async function assertAgenciaPermitida(userId: string, agenciaId: string) {
  if ((await currentUserAgenciaIds(userId)).includes(agenciaId)) return;
  throw new Error("No perteneces a la agencia indicada.");
}

export const createAnuncio = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => AnuncioInput.parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const { contactos, uso_local_ids, modo_publicacion, ...rest } = data;
    const userId = await resolveCurrentUserId();
    if (!userId) throw new Error("Debes iniciar sesión.");
    // El staff fija cualquier estado; el resto crea como "pendiente" salvo lo que
    // permita su agencia (ver más abajo).
    const isStaff = await userIsStaff(userId);
    const publication = modo_publicacion ? publicationPayload(rest, modo_publicacion) : rest;
    if (publication.visibilidad === "off_market" && !isStaff) {
      await requireVipAccess();
    }
    let payload: any = isStaff && !modo_publicacion ? { ...publication } : { ...publication, estado: "pendiente" as const };
    // En "Publicar" de My Bizaclia (público u off-market) el anunciante es siempre
    // quien publica: el formulario no decide contactos, agencia ni asignación.
    const asignadoExplicito: string | null = modo_publicacion ? null : payload.asignado_a ?? null;
    delete payload.asignado_a;
    if (modo_publicacion) payload.agencia_id = null;

    const contactosFinal = (modo_publicacion ? [] : contactos).map((c) => ({
      contacto_id: c.contacto_id,
      rol: c.rol,
      rol_custom: c.rol === "otro" ? c.rol_custom ?? null : null,
    }));
    // Sin anunciante explícito, el anunciante es el contacto de quien crea el anuncio.
    let anunciante = contactosFinal.find((c) => c.rol === "anunciante");
    if (!anunciante) {
      anunciante = { contacto_id: await contactoDeUsuario(userId), rol: "anunciante", rol_custom: null };
      contactosFinal.push(anunciante);
    }
    // Agencia: la indicada (validada) o, si no hay, la agencia a la que pertenece el anunciante.
    if (payload.agencia_id) {
      if (!isStaff) await assertAgenciaPermitida(userId, payload.agencia_id);
    } else {
      payload.agencia_id = await agenciaDeContacto(anunciante.contacto_id);
    }
    // Los miembros de la agencia controlan el estado de sus anuncios (publicar,
    // despublicar, borrar); fuera de su agencia el anuncio entra como "pendiente".
    if (
      !isStaff &&
      !modo_publicacion &&
      payload.agencia_id &&
      esEstadoAgencia(publication.estado) &&
      (await currentUserAgenciaIds(userId)).includes(payload.agencia_id)
    ) {
      payload.estado = publication.estado;
    }
    // Autoasignación al creador (staff o miembro editor+ de la agencia).
    const { resolveAsignadoInicial } = await import("@/lib/asignacion.server");
    const asignadoA = await resolveAsignadoInicial(
      userId,
      payload.agencia_id ?? null,
      asignadoExplicito,
    );
    const { data: row, error } = await sb
      .from("anuncios")
      .insert({ ...payload, asignado_a: asignadoA, created_by: userId })
      .select("id, referencia")
      .single();
    if (error) throw new Error(error.message);

    await syncAnuncioContactos(row.id, contactosFinal);
    await syncAnuncioUsosLocal(row.id, uso_local_ids);

    return { ok: true, id: row.id, referencia: row.referencia };
  });

// ───────── Update ─────────
export const updateAnuncio = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => AnuncioInput.extend(Id.shape).parse(d))
  .handler(async ({ data }) => {
    const sb = getSupabaseAdmin();
    const { id, contactos, uso_local_ids, modo_publicacion, ...rest } = data;
    await assertCanAccessAnuncio(id);
    const userId = await resolveCurrentUserId();
    const isStaff = await userIsStaff(userId);
    let payload: typeof rest = modo_publicacion ? publicationPayload(rest, modo_publicacion) : rest;
    if (modo_publicacion) {
      // El formulario de "Publicar" no gestiona anunciante, agencia ni asignación:
      // se conservan los fijados al crear el anuncio.
      const { agencia_id: _agencia, asignado_a: _asignado, ...propios } = payload;
      payload = propios as typeof rest;
    }
    if (!isStaff || modo_publicacion) {
      // Preservar la visibilidad (y el estado, salvo lo que permita la agencia).
      const { data: current } = await sb
        .from("anuncios")
        .select("estado, visibilidad, created_by, agencia_id")
        .eq("id", id)
        .single();
      if (!current) throw new Error("No se encontró el anuncio.");
      if (!isStaff && payload.agencia_id && payload.agencia_id !== current.agencia_id) {
        await assertAgenciaPermitida(userId!, payload.agencia_id);
      }
      if (modo_publicacion && (current.created_by !== userId || current.visibilidad !== modo_publicacion)) {
        throw new Error("Este anuncio no pertenece a tu sección de publicación.");
      }
      // Un miembro de la agencia del anuncio puede moverlo entre los estados de
      // agencia; los estados de moderación de Bizaclia no se tocan.
      const agenciaFinal = payload.agencia_id ?? current.agencia_id;
      const puedeCambiarEstado =
        !isStaff &&
        !modo_publicacion &&
        !!agenciaFinal &&
        esEstadoAgencia(current.estado) &&
        esEstadoAgencia(payload.estado) &&
        (await currentUserAgenciaIds(userId!)).includes(agenciaFinal);
      payload = {
        ...payload,
        estado: puedeCambiarEstado ? payload.estado : current.estado,
        visibilidad: current.visibilidad,
      } as typeof rest;
      if (current.visibilidad === "off_market") payload.autoriza_intermediacion = false;
    }
    const { data: before } = await sb
      .from("anuncios")
      .select("asignado_a")
      .eq("id", id)
      .maybeSingle();
    const { data: after, error } = await sb
      .from("anuncios")
      .update(payload)
      .eq("id", id)
      .select("id, asignado_a")
      .single();
    if (error) throw new Error(error.message);
    if (!modo_publicacion) {
      await syncAnuncioContactos(
        id,
        contactos.map((c) => ({
          contacto_id: c.contacto_id,
          rol: c.rol,
          rol_custom: c.rol === "otro" ? c.rol_custom ?? null : null,
        })),
      );
    }
    await syncAnuncioUsosLocal(id, uso_local_ids);
    return {
      ok: true,
      asignado_a: after?.asignado_a ?? null,
      asignado_a_prev: before?.asignado_a ?? null,
    };
  });


// ───────── Proceso ─────────
export const setAnuncioProceso = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ id: z.string().uuid(), proceso: z.enum(ANUNCIO_PROCESOS) }).parse(d),
  )
  .handler(async ({ data }) => {
    await assertCanAccessAnuncio(data.id);
    const sb = getSupabaseAdmin();
    const { error } = await sb
      .from("anuncios")
      .update({ proceso: data.proceso })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Reasignar ─────────
export const assignAnuncio = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z
      .object({ id: z.string().uuid(), asignado_a: z.string().uuid().nullable() })
      .parse(d),
  )
  .handler(async ({ data }) => {
    await assertCanAccessAnuncio(data.id);
    const sb = getSupabaseAdmin();
    const { error } = await sb
      .from("anuncios")
      .update({ asignado_a: data.asignado_a })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Delete ─────────
export const deleteAnuncio = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => Id.parse(d))
  .handler(async ({ data }) => {
    await assertCanAccessAnuncio(data.id);
    const sb = getSupabaseAdmin();

    // 1) Obtener todos los media del anuncio (imágenes + watermarks + vídeos de Stream)
    const { data: mediaRows, error: eMedia } = await sb
      .from("media")
      .select("id, storage_path, watermark_path, stream_video_id")
      .eq("anuncio_id", data.id);
    if (eMedia) throw new Error(eMedia.message);

    // 2) Borrar vídeos en Cloudflare Stream
    const streamIds = (mediaRows ?? [])
      .map((r) => r.stream_video_id)
      .filter((v): v is string => !!v);
    if (streamIds.length) {
      const { deleteStreamVideo } = await import("./media.server");
      await Promise.all(
        streamIds.map((id) =>
          deleteStreamVideo(id).catch((err: unknown) =>
            console.error("Error borrando Stream video:", id, err),
          ),
        ),
      );
    }

    // 3) Borrar archivos del bucket (imagen original + marca de agua)
    const paths: string[] = [];
    for (const r of mediaRows ?? []) {
      if (r.storage_path) paths.push(r.storage_path);
      if (r.watermark_path) paths.push(r.watermark_path);
    }
    if (paths.length) {
      const { error: eStorage } = await sb.storage.from("media").remove(paths);
      if (eStorage) console.error("Error borrando de storage:", eStorage.message);
    }

    // 4) Borrar el anuncio (las filas de media se borran por FK cascade,
    //    pero las eliminamos explícitamente por si la cascade no está configurada)
    if (mediaRows && mediaRows.length) {
      await sb.from("media").delete().eq("anuncio_id", data.id);
    }
    const { error } = await sb.from("anuncios").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ───────── Contexto de administración (¿es admin? ¿es staff de Bizaclia?) ─────────
export const getAnunciosAdminContext = createServerFn({ method: "GET" }).handler(async () => {
  const userId = await resolveCurrentUserId();
  if (!userId) throw new Error("Unauthorized");
  const sb = getSupabaseAdmin();
  const { data: roles } = await sb.from("user_roles").select("role").eq("user_id", userId);
  const rs = (roles ?? []).map((r: { role: string }) => r.role);
  const isAdmin = rs.includes("admin");
  const isStaff = isAdmin || rs.includes("customer_service") || rs.includes("agente");
  return { isAdmin, isStaff };
});
